import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, userAPI } from '../api/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
      loadUserInfo();
    } else {
      localStorage.removeItem('token');
      setUser(null);
    }
  }, [token]);

  const loadUserInfo = async () => {
    if (!token) return;
    
    try {
      const userData = await userAPI.getProfile();
      setUser(userData);
      // Oturum gecerli: Face ID aciksa bu cihazin anahtarini tazele.
      import('../utils/faceId').then((m) => m.faceIdAnahtariniTazele('personel')).catch(() => {});
    } catch (error) {
      console.error('Error loading user info:', error);
      // Don't clear token on error, just log it
    }
  };

  const login = async (email, password, username) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.login(email, password, username);
      // Backend now returns { token } object
      const tokenValue = response.token || response;

      /* JETON ÖNCE DİSKE, SONRA DURUMA.
         setToken yalnızca React durumunu değiştirir; localStorage'a yazan
         useEffect bir sonraki çizimde çalışır. Oysa hemen aşağıdaki
         loadUserInfo, isteğe jetonu localStorage'dan okuyup ekliyor —
         yani ESKİ jetonla gidiyordu. Eski jeton geçersizse (başka bir
         cihazdan giriş yapılmışsa) sunucu 401 döndürüyor, araya giren
         hata yakalayıcı da oturumu silip giriş sayfasına atıyordu:
         kullanıcı doğru şifreyi yazmasına rağmen giriş yapamıyordu. */
      try { localStorage.setItem('token', tokenValue); } catch (_) { /* yoksay */ }

      setToken(tokenValue);
      // Load user info after login
      await loadUserInfo();
      return { success: true };
    } catch (err) {
      console.error('Login error in AuthContext:', err);
      
      // Detaylı hata mesajı oluştur
      let errorMessage = err.response?.data?.message || err.message || 'Giriş başarısız';
      
      // Validation errors varsa onları da ekle
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        const validationErrors = err.response.data.errors.join(', ');
        errorMessage += ` (${validationErrors})`;
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    // Ana ekran widget'i oturumla birlikte kapanir; aksi halde cikis
    // yapilmis gorunurken widget veri gostermeye devam eder.
    import('../utils/widgetKoprusu').then((m) => m.widgetJetonuSil()).catch(() => {});
    // Face ID girisi cikista KAPATILMIYOR. Eskiden kapatiliyordu: cikis
    // yapan kullanici bir sonraki giriste yine sifre yaziyor, girdikten
    // sonra da "Face ID acilsin mi?" karti cikiyordu ("girdikten sonra bi
    // anlami kalmiyo"). Kapatmak isteyen Ayarlar'daki Face ID anahtarini
    // kullanir; yuz sorulmadan kimse giremez.
  };

  /**
   * Sifre sormadan, elde hazir bir jetonla oturum acar.
   * Face ID girisi icin: yuz dogrulanip korumali alandan okunan jeton
   * buraya veriliyor. Jeton gecersizse profil cagrisi hata verir ve
   * kullanici klasik giris ekraninda kalir.
   */
  const jetonlaGir = async (hazirJeton) => {
    if (!hazirJeton) return { success: false };
    // Girisle ayni sira: jeton once diske (istekler oradan okuyor).
    try { localStorage.setItem('token', hazirJeton); } catch (_) { /* yoksay */ }
    setToken(hazirJeton);
    return { success: true };
  };

  const value = {
    token,
    user,
    login,
    jetonlaGir,
    logout,
    loadUserInfo,
    loading,
    error,
    isAuthenticated: !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};